<template>
  <section ref="sectionRef" class="links-with-images-block custom-section">
    <div class="container">
      <h2
        v-if="title"
        class="custom-section__title"
        v-html="title"
      />
      <div class="links-with-images-block__swiper">
        <swiper
          :modules="swiperModules"
          :scrollbar="{ draggable: true }"
          :mousewheel="true"
          :space-between="0"
          :slides-per-view="'auto'"
          :watch-slides-progress="true"
        >
          <template
            v-for="(item, index) in items"
            :key="`links-with-images-block__list-${index}`"
          >
            <swiper-slide>
              <nuxt-link
                :to="item.link"
                :prefetch="index < 3"
                class="links-with-image-card"
              >
                <div class="links-with-image-card__image">
                  <nuxt-img
                    :src="String(item.image)"
                    :alt="item.title || 'Категория'"
                    width="200"
                    height="130"
                    :loading="index < 4 ? 'eager' : 'lazy'"
                    :fetchpriority="index < 2 ? 'high' : 'auto'"
                    decoding="async"
                    format="webp"
                    :quality="index < 4 ? 85 : 75"
                    sizes="(max-width: 619px) 152px, 197px"
                    fit="cover"
                    :placeholder="index > 3 ? [50, 25, 75, 5] : undefined"
                  />
                </div>
                <div
                  class="links-with-image-card__body"
                  v-html="item.title"
                />
              </nuxt-link>
            </swiper-slide>
          </template>
        </swiper>
      </div>
      <div class="links-with-images-block__mobile-message">
        Выберите нужный вам раздел выше
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { IBlockDefaultProps } from '#sitis/internal/models/common';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Mousewheel, Scrollbar } from 'swiper/modules';
import { useIntersectionObserver } from '@vueuse/core';

const props = defineProps<IBlockDefaultProps>();

// Refs
const sectionRef = ref<HTMLElement | null>(null);
const isVisible = ref(false);

// Computed
const title = computed(() => props?.values?.title || '');
const items = computed(() => {
  const list = props?.values?.items || [];
  return list;
});

// Swiper modules - ленивая инициализация
const swiperModules = [Mousewheel, Scrollbar];

// Intersection Observer для отложенной загрузки
if (process.client) {
  const { stop } = useIntersectionObserver(
    sectionRef,
    ([{ isIntersecting }]) => {
      if (isIntersecting && !isVisible.value) {
        isVisible.value = true;
        // Останавливаем наблюдение после первой загрузки
        stop();
      }
    },
    {
      threshold: 0.1,
      rootMargin: '50px'
    }
  );
}

// На сервере показываем сразу для SSR
if (process.server) {
  isVisible.value = true;
}
</script>

<style lang="scss">
.links-with-images-block {
  contain: layout style;
}

.links-with-images-block__swiper {
  .swiper {
    margin: 0 calc((100vw - var(--width-container)) / 2 * -1);
    padding: 0 calc((100vw - var(--width-container)) / 2);
  }
  .swiper-slide {
    display: flex;
    width: 197px;
    max-width: 197px;
    min-width: 197px;
    margin-right: 20px;
    height: auto;
    contain: layout style paint;
  }
  .swiper-scrollbar {
    position: initial;
    margin-top: 32px;
    height: 1px;
    background: #F4F3F1;
  }
  .swiper-scrollbar .swiper-scrollbar-drag {
    background: #B8B8B8;
  }
}

// Skeleton loader стили
.links-with-images-block__skeleton {
  display: flex;
  gap: 20px;
  margin: 0 calc((100vw - var(--width-container)) / 2 * -1);
  padding: 0 calc((100vw - var(--width-container)) / 2);

  .skeleton-card {
    width: 197px;
    height: 224px;
    min-width: 197px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: loading 1.5s ease-in-out infinite;
    border-radius: 20px;
  }
  .skeleton-card__media {
    display: flex;
    overflow: hidden;
    position: relative;

    &:after {
      content: "";
      float: left;
      padding-top: 65.99%;
    }
  }
  .skeleton-card__body {
    min-height: 100px;
  }
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
.links-with-images-block__mobile-message {
  display: none;

  margin-top: 16px;
  font-weight: 500;
  font-size: 12px;
  line-height: 125%;
  color: #989898;
}

.links-with-image-card {
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  background: #F4F3F1;
  border-radius: 20px;
  height: auto;
  width: 100%;
  min-height: 224px;
  border: 1px solid #F4F3F1;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  will-change: transform;
  contain: layout style paint;
}
.links-with-image-card__image {
  position: relative;
  overflow: hidden;
  border-radius: 0 0 20px 20px;
  background: #f0f0f0;

  img {
    position: absolute;
    top: 0; left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    will-change: transform;
    transition: transform 0.3s ease;
  }
  &:after {
    content: "";
    float: left;
    padding-top: 65.99%;
  }
}

.links-with-image-card:hover .links-with-image-card__image img {
  transform: scale(1.05);
}

.links-with-image-card__body {
  padding: 24px;
  box-sizing: border-box;

  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  color: #000000;
}

@media (max-width: 1023px) {
  .links-with-images-block__swiper {
    .swiper {
      margin: 0 calc(((100vw - var(--width-container)) / 2 + 20px) * -1);
      padding: 0 calc(((100vw - var(--width-container)) / 2 + 20px));
    }
  }
  .links-with-images-block__mobile-message {
    display: block;
  }

  .links-with-image-card__body {
    padding: 16px;
  }
}
@media (max-width: 859px) {
  .links-with-images-block__swiper {
    .swiper {
      margin: 0 calc(((100vw - var(--width-container)) / 2 + 10px) * -1);
      padding: 0 calc(((100vw - var(--width-container)) / 2 + 10px));
    }
  }
}
@media (max-width: 619px) {
  .links-with-images-block__swiper {
    .swiper {
      margin: 0 -20px;
      padding: 0 20px;
    }
    .swiper-slide {
      width: 152px;
      max-width: 152px;
      min-width: 152px;
      margin-right: 16px;
    }
  }

  .links-with-images-block__skeleton {
    margin: 0 -20px;
    padding: 0 20px;

    .skeleton-card {
      width: 152px;
      min-width: 152px;
    }
  }
}
</style>
